import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Briefcase, Calendar, Check, Loader2, Phone, Plus, UtensilsCrossed, X } from "lucide-react";
import { toast } from "sonner";
import type { OpenItem, RecordSummary } from "@shared/recordSummary";
import { Button } from "@/components/ui/button";
import { useUsers } from "@/hooks/useUsers";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { RecordPanel } from "./RecordFrame";
import { currentRecordRef, dayLabel, firstName, localYmd, nextStepBody, targetLinkRefs } from "./recordFormat";
import { TaskLinkChips } from "@/components/tasks/TaskLinkChips";
import { RecordLinkPicker } from "@/components/tasks/RecordLinkPicker";
import { TaskDetailSheet } from "@/components/tasks/TaskDetailSheet";
import { linksExcept, taskLinkLabel, type TaskLink } from "@shared/taskLinks";

const ICON = { call: Phone, meeting: Calendar, conference: Briefcase } as const;

interface Props {
  summary: RecordSummary | undefined;
  loading: boolean;
  error?: boolean;
  onRetry?: () => void;
  openLabel: string;
  target: { contactId?: string; companyId?: string };
  nextStepOpen: boolean;
  onNextStepOpenChange: (open: boolean) => void;
}

/** Prep for a call: the last conversation, and everything still open on this record. */
export function BriefPanel({ summary, loading, error, onRetry, openLabel, target, nextStepOpen, onNextStepOpenChange }: Props) {
  const today = localYmd(new Date());
  const c = summary?.lastConversation;
  const Icon = c ? (c.meal ? UtensilsCrossed : ICON[c.type]) : null;
  const others = c?.people.filter((p) => p.id !== target.contactId) ?? [];

  if (error) {
    return (
      <RecordPanel>
        <p className="text-[13px] text-muted-foreground">
          Couldn't load this summary.{" "}
          {onRetry && <button type="button" onClick={onRetry} className="font-semibold text-primary-text hover:underline">Retry</button>}
        </p>
      </RecordPanel>
    );
  }

  return (
    <RecordPanel>
      <div className="grid gap-3 md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] md:gap-0">
        <div className="min-w-0 md:pr-3.5">
          <div className="mb-1.5 flex items-center justify-between">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Last conversation</h3>
            {c && <span className="text-xs tabular-nums text-muted-foreground">{dayLabel(c.date, today)}</span>}
          </div>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : c && Icon ? (
            <>
              <div className="flex items-center gap-1.5 text-[13.5px] font-semibold"><Icon className="h-3.5 w-3.5 shrink-0" />{c.title || "Untitled"}</div>
              <div className="ml-5 text-xs text-muted-foreground">
                {[c.loggedBy, others.length ? `with ${others.map((p) => p.name).join(", ")}` : null].filter(Boolean).join(", ")}
              </div>
              {c.content && <p className="ml-5 mt-1.5 line-clamp-4 whitespace-pre-line text-[13px] text-muted-foreground">{c.content}</p>}
            </>
          ) : (
            <p className="text-[13px] text-muted-foreground">No call or meeting logged yet.</p>
          )}
        </div>
        <div className="min-w-0 border-t pt-3 md:border-l md:border-t-0 md:pl-3.5 md:pt-0">
          <OpenItems
            label={openLabel}
            items={summary?.openItems ?? []}
            target={target}
            formOpen={nextStepOpen}
            onFormOpenChange={onNextStepOpenChange}
          />
        </div>
      </div>
    </RecordPanel>
  );
}

function OpenItems({ label, items, target, formOpen, onFormOpenChange }: {
  label: string; items: OpenItem[]; target: Props["target"]; formOpen: boolean; onFormOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  // The record this panel is on, left out of each task's chips.
  const current = currentRecordRef(target);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["record-summary"] });
    qc.invalidateQueries({ queryKey: ["/api/tasks"] });
    qc.invalidateQueries({ queryKey: ["/api/prospects"] });
    qc.invalidateQueries({ queryKey: ["reminders"] });
    qc.invalidateQueries({ queryKey: ["entityReminders"] });
  };
  const complete = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/tasks/${id}/complete`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error("Couldn't mark it done");
    },
    onSuccess: () => { refresh(); toast.success("Marked done"); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span className="truncate" title={label}>{label}</span><span className="shrink-0 font-medium normal-case tracking-normal tabular-nums">{items.length}</span>
        </h3>
        {!formOpen && (
          <button type="button" onClick={() => onFormOpenChange(true)} className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-primary-text hover:underline">
            <Plus className="h-3 w-3" />Next step
          </button>
        )}
      </div>
      {items.length === 0 && !formOpen && <p className="text-[13px] text-muted-foreground">Nothing open.</p>}
      <ul className="grid gap-0.5">
        {items.map((i) => (
          <li key={i.id} className="grid grid-cols-[18px_minmax(0,1fr)_auto] items-start gap-2 py-1">
            <button
              type="button"
              disabled={complete.isPending}
              onClick={() => complete.mutate(i.id)}
              aria-label={`Mark done: ${i.title}`}
              className="mt-0.5 grid h-3.5 w-3.5 place-items-center rounded border-[1.5px] border-muted-foreground/60 hover:border-emerald-600"
            >
              {complete.isPending && complete.variables === i.id && <Check className="h-2.5 w-2.5" />}
            </button>
            <div className="min-w-0">
              <button type="button" onClick={() => setOpenId(i.id)} className="text-left text-[13px] hover:underline">
                {i.title}{i.isReminder && <span className="ml-1 text-[10.5px] text-muted-foreground">Reminder</span>}
              </button>
              {/* The task's other records: on Barnes's page, "Northridge Partners". */}
              <TaskLinkChips links={linksExcept(i.links ?? [], current)} max={2} className="mt-0.5" />
            </div>
            <span className={cn("whitespace-nowrap text-xs tabular-nums text-muted-foreground", i.overdue && "font-semibold text-destructive")}>
              {[firstName(i.ownerName), i.dueOn ? `${i.overdue ? "was due " : ""}${dayLabel(i.dueOn, localYmd(new Date()))}` : null].filter(Boolean).join(" · ")}
            </span>
          </li>
        ))}
      </ul>
      {formOpen && <NextStepForm target={target} onDone={() => { onFormOpenChange(false); refresh(); }} onCancel={() => onFormOpenChange(false)} />}
      {/* The same panel the Tasks page opens, so links (and anything else) can be fixed from here. */}
      <TaskDetailSheet id={openId} onClose={() => setOpenId(null)} />
    </>
  );
}

function NextStepForm({ target, onDone, onCancel }: { target: Props["target"]; onDone: () => void; onCancel: () => void }) {
  const { user } = useAuth();
  const { data: users = [] } = useUsers();
  const [title, setTitle] = useState("");
  const [ownerId, setOwnerId] = useState<string>("");
  const [due, setDue] = useState("");
  // Records besides this one, e.g. the buyer on a client's page. This record
  // is linked through `target` and is never offered in the picker.
  const [extra, setExtra] = useState<TaskLink[]>([]);
  const owner = ownerId || user?.id || "";
  // Both of the target's records on a person page: the body sends both.
  const self = targetLinkRefs(target);
  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/tasks", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nextStepBody({
          title: title.trim(), ownerId: owner, dueYmd: due || null, ...target,
          links: extra.map(({ kind, id }) => ({ kind, id })),
        })),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Couldn't add the next step");
    },
    onSuccess: () => { toast.success("Next step added"); onDone(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const inputCls = "min-w-0 rounded-md border bg-background px-2 py-1 text-[12.5px]";
  return (
    <form
      className="mt-2 flex flex-wrap gap-1.5"
      onSubmit={(e) => { e.preventDefault(); if (title.trim() && owner) save.mutate(); }}
    >
      <input id="next-step-title" autoFocus required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs to happen" aria-label="Next step" className={cn(inputCls, "flex-[1_1_180px]")} />
      <select id="next-step-owner" value={owner} onChange={(e) => setOwnerId(e.target.value)} aria-label="Owner" className={inputCls}>
        {users.map((u) => <option key={u.id} value={u.id}>{firstName(u.name)}</option>)}
      </select>
      <input id="next-step-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date" className={inputCls} />
      <Button type="submit" size="sm" disabled={save.isPending || !title.trim() || !owner}>Add</Button>
      <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      <div className="flex w-full flex-wrap items-center gap-1">
        {extra.map((l) => (
          <span key={`${l.kind}:${l.id}`} className="inline-flex max-w-[220px] items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] leading-none text-muted-foreground">
            <span className="truncate" title={taskLinkLabel(l)}>{taskLinkLabel(l)}</span>
            <button
              type="button"
              aria-label={`Unlink ${taskLinkLabel(l)}`}
              onClick={() => setExtra(extra.filter((x) => !(x.kind === l.kind && x.id === l.id)))}
              className="hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {/* This record counts toward the cap, so the picker gets it as already taken. */}
        <RecordLinkPicker value={extra} onChange={setExtra} exclude={self}>
          <button type="button" className="inline-flex items-center gap-1 text-xs font-medium text-primary-text hover:underline">
            <Plus className="h-3 w-3" />Link another record
          </button>
        </RecordLinkPicker>
      </div>
    </form>
  );
}
